import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-border bg-clip-padding text-sm font-medium whitespace-nowrap shadow-xs transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "border-none  shadow-none hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
        // Used by /settings/billing to match the design's elevated primary
        // CTA: solid primary fill with an inset top highlight + a soft drop
        // shadow that gives the button a subtle "raised pill" feel.
        polished:
          "bg-primary text-primary-foreground border-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_2px_rgba(15,17,26,0.08)] hover:bg-primary/90 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_1px_2px_rgba(0,0,0,0.4)]",
        // Quiet companion to `polished` — surface fill, stronger border, no
        // hover-tint chrome. Use for "Add payment method", "Cancel
        // subscription", and any secondary action sitting next to a
        // `polished` primary CTA.
        "polished-outline":
          "bg-card text-foreground border-input hover:bg-muted/40 hover:text-foreground dark:bg-input/30 dark:hover:bg-input/50",
        // Inline text-link button used across all settings pages — the old
        // local `PlainTextButton`. Underlined, no chrome, sized inline. The
        // `!` suffix overrides height/padding from the size variant so this
        // sits flush in copy or sticky footers regardless of `size`.
        plain:
          "h-auto! border-none bg-transparent px-0! py-0! shadow-none text-[13.5px] font-medium text-foreground underline underline-offset-[3px] decoration-border hover:text-foreground/80 disabled:opacity-40",
        // Muted twin of `plain` — same chrome, secondary text colour. Used
        // for low-emphasis actions like "Discard" or "Make default".
        "plain-muted":
          "h-auto! border-none bg-transparent px-0! py-0! shadow-none text-[13.5px] font-medium text-muted-foreground underline underline-offset-[3px] decoration-border hover:text-foreground disabled:opacity-40",
      },
      size: {
        default:
          "h-10 cursor-pointer gap-2 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 rounded-lg",
        xs: "h-6 cursor-pointer gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 cursor-pointer gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-12 cursor-pointer gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-10 cursor-pointer",
        "icon-xs":
          "size-6 cursor-pointer rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 cursor-pointer rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9 cursor-pointer",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
